import React, { useState } from "react";

const MonthlyTotalsTable = ({ selectedBusiness, businessData, excludedCategories = [], onShowDetails }) => {
	const [expandedCategories, setExpandedCategories] = useState({});
	const [allExpanded, setAllExpanded] = useState(false);

	if (!businessData || !businessData.categories) return null;

	const excludedSet = Array.isArray(excludedCategories) && excludedCategories.length > 0 ? new Set(excludedCategories.map((c) => String(c).trim().toLowerCase())) : null;
	const monthsDenominator = businessData?.monthsInRange || 12;
	const categories = excludedSet
		? businessData.categories
				.map((cat) => {
					const hasSubs = Array.isArray(cat.subcategories) && cat.subcategories.length > 0;
					if (hasSubs) {
						const filteredSubs = cat.subcategories.filter((sc) => !excludedSet.has(String(sc.category).trim().toLowerCase()));
						if (filteredSubs.length === 0) return null;
						const len = cat.monthlyTotals?.length || 12;
						const recomputedMonthlyTotals = Array(len).fill(0);
						filteredSubs.forEach((sc) => {
							sc.monthlyTotals.forEach((amt, idx) => {
								recomputedMonthlyTotals[idx] += amt;
							});
						});
						const total = recomputedMonthlyTotals.reduce((s, v) => s + v, 0);
						return {
							...cat,
							subcategories: filteredSubs,
							monthlyTotals: recomputedMonthlyTotals,
							total,
							monthlyAverage: total / monthsDenominator,
						};
					}
					if (excludedSet.has(String(cat.category).trim().toLowerCase())) return null;
					const total = Array.isArray(cat.monthlyTotals) ? cat.monthlyTotals.reduce((s, v) => s + v, 0) : cat.total || 0;
					return {
						...cat,
						total,
						monthlyAverage: total / monthsDenominator,
					};
				})
				.filter(Boolean)
		: businessData.categories;

	const toggleCategory = (categoryName) => {
		setExpandedCategories((prev) => ({
			...prev,
			[categoryName]: !prev[categoryName],
		}));
	};

	const handleExpandCollapseAll = () => {
		const newState = {};
		categories.forEach((cat) => {
			if (cat.subcategories && cat.subcategories.length > 0) {
				newState[cat.category] = !allExpanded;
			}
		});
		setExpandedCategories(newState);
		setAllExpanded(!allExpanded);
	};

	const sortedMonths = Array.from({ length: 12 }, (_, idx) => {
		const date = new Date(2024, idx);
		return date.toLocaleString("en-US", { month: "short" });
	});

	const monthlyTotalsRow = Array(12).fill(0);

	categories.forEach((category) => {
		category.monthlyTotals.forEach((amount, idx) => {
			monthlyTotalsRow[idx] += amount;
		});
	});

	// Helper to retrieve all contributing entries for a category cell by month index
	const getEntriesForMonth = (category, monthIdx) => {
		const results = [];
		const pushIfMatch = (entry) => {
			if (!entry || !entry.date) return;
			const d = new Date(entry.date);
			if (d.getMonth() === monthIdx) results.push(entry);
		};
		if (Array.isArray(category.subcategories) && category.subcategories.length > 0) {
			category.subcategories.forEach((sc) => {
				if (Array.isArray(sc.entries)) sc.entries.forEach(pushIfMatch);
			});
		} else if (Array.isArray(category.entries)) {
			category.entries.forEach(pushIfMatch);
		}
		return results;
	};

	const getEntriesForFooterMonth = (monthIdx) => {
		const results = [];
		categories.forEach((cat) => {
			const arr = getEntriesForMonth(cat, monthIdx);
			if (arr.length) results.push(...arr);
		});
		return results;
	};

	const grandTotal = monthlyTotalsRow.reduce((sum, val) => sum + val, 0);
	const grandAverage = grandTotal / monthsDenominator;

	const formatCurrency = (val) => Math.round(val).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 });

	return (
		<div className="v4-spending-table__scroll">
			<table className="v4-spending-table">
				<thead>
					<tr className="v4-spending-table__header-row">
						<th className="v4-spending-table__header-cell v4-spending-table__header-cell--sticky">
							<button className="v4-spending-table__expand-all" onClick={handleExpandCollapseAll}>
								{allExpanded ? "−" : "+"}
							</button>
						</th>
						{sortedMonths.map((monthName, idx) => (
							<th className="v4-spending-table__header-cell" key={idx}>
								{monthName}
							</th>
						))}
						<th className="v4-spending-table__header-cell v4-spending-table__header-cell--total">Total</th>
						<th className="v4-spending-table__header-cell v4-spending-table__header-cell--avg">Avg</th>
					</tr>
				</thead>
				<tbody>
					{categories.map((category) => {
						const isExpanded = expandedCategories[category.category] || false;
						const hasSubcategories = category.subcategories && category.subcategories.length > 0;

						return (
							<React.Fragment key={category.category}>
								{/* Main Category Row */}
								<tr className="v4-spending-table__row--category">
									<td
										className="v4-spending-table__sticky-col"
										onClick={() => hasSubcategories && toggleCategory(category.category)}
									>
										{category.category}
										{hasSubcategories && <span className="v4-spending-table__expand-toggle">{isExpanded ? "−" : "+"}</span>}
									</td>
									{category.monthlyTotals.map((val, idx) => (
										<td
											onClick={() => {
												if (onShowDetails)
													onShowDetails({
														entries: getEntriesForMonth(category, idx),
														title: `${category.category} | ${sortedMonths[idx]}`,
													});
											}}
											className={`v4-spending-table__cell v4-spending-table__cell--clickable ${val === 0 ? "v4-spending-table__cell--zero" : ""}`}
											key={idx}
										>
											{formatCurrency(val)}
										</td>
									))}
									<td className="v4-spending-table__cell v4-spending-table__cell--total">{formatCurrency(category.total)}</td>
									<td className="v4-spending-table__cell v4-spending-table__cell--avg">{formatCurrency(category.monthlyAverage)}</td>
								</tr>

								{/* Subcategories */}
								{hasSubcategories &&
									category.subcategories.map((subcat) => (
										<tr key={subcat.category} className={`v4-spending-table__row--subcategory ${isExpanded ? "expanded" : "collapsed"}`}>
											<td className="v4-spending-table__sticky-col">{subcat.category}</td>
											{subcat.monthlyTotals.map((val, idx) => (
												<td
													className={`v4-spending-table__cell v4-spending-table__cell--clickable ${val === 0 ? "v4-spending-table__cell--zero" : ""}`}
													key={idx}
													onClick={() => {
														if (onShowDetails)
															onShowDetails({
																entries: getEntriesForMonth(subcat, idx),
																title: `${subcat.category} | ${sortedMonths[idx]}`,
															});
													}}
												>
													{formatCurrency(val)}
												</td>
											))}
											<td className="v4-spending-table__cell v4-spending-table__cell--total">{formatCurrency(subcat.total)}</td>
											<td className="v4-spending-table__cell v4-spending-table__cell--avg">{formatCurrency(subcat.monthlyAverage)}</td>
										</tr>
									))}
							</React.Fragment>
						);
					})}

					{/* Footer Row */}
					<tr className="v4-spending-table__row--footer">
						<td className="v4-spending-table__sticky-col">Monthly Totals</td>
						{monthlyTotalsRow.map((val, idx) => (
							<td
								className="v4-spending-table__cell v4-spending-table__cell--clickable"
								key={idx}
								onClick={() => {
									if (onShowDetails)
										onShowDetails({
											entries: getEntriesForFooterMonth(idx),
											title: `Monthly Totals | ${sortedMonths[idx]}`,
										});
								}}
							>
								{formatCurrency(val)}
							</td>
						))}
						<td className="v4-spending-table__cell v4-spending-table__cell--total">{formatCurrency(grandTotal)}</td>
						<td className="v4-spending-table__cell v4-spending-table__cell--avg">{formatCurrency(grandAverage)}</td>
					</tr>
				</tbody>
			</table>
		</div>
	);
};

export default MonthlyTotalsTable;
